import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CText } from '../../components/common';
import { PortfolioHolding } from '../../api/portfolio';
import { getCompanyDetails, queryStockResearch, CompanyDetails, CompanyInsights } from '../../api/copilot';
import {
  FiUsers,
  FiAlertTriangle,
  FiChevronDown,
  FiChevronUp,
  FiBookOpen,
} from 'react-icons/fi';

interface Props {
  holding: PortfolioHolding | null;
  holdings: PortfolioHolding[];
  colors: any;
  onSelectStock: (h: PortfolioHolding) => void;
}

const formatINR = (value: number): string => {
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 10000000) {
    formatted = (abs / 10000000).toFixed(2) + ' Cr';
  } else if (abs >= 100000) {
    formatted = (abs / 100000).toFixed(2) + ' L';
  } else {
    formatted = abs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return (value < 0 ? '-' : '') + '\u20B9' + formatted;
};

interface AnalysisSection {
  title: string;
  content: string;
  color: string; // left border color
}

// ── Helper: extract row from financial tables by label match ──
function findMetricRow(
  labels: readonly string[],
  ...tables: (Array<Record<string, any>> | undefined)[]
): Record<string, any> | undefined {
  for (const table of tables) {
    if (!table || table.length === 0) continue;
    const labelKey = Object.keys(table[0])[0];
    const found = table.find(row => {
      const name = String(row[labelKey] || '').toLowerCase();
      return labels.some(m => name.includes(m));
    });
    if (found) return found;
  }
  return undefined;
}

// ── Helper: extract last N year values from a table row ──
function extractRecentValues(row: Record<string, any>, n: number): string {
  const labelKey = Object.keys(row)[0];
  const entries: { period: string; value: string }[] = [];
  for (const [key, raw] of Object.entries(row)) {
    if (key === labelKey || key === 'TTM') continue;
    const val = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
    if (!isNaN(val)) entries.push({ period: key, value: String(raw) });
  }
  return entries.slice(-n).map(e => `${e.period}: ${e.value}`).join(', ');
}

// ── Build financial context string for AI prompt ──
function buildFinancialContext(details: CompanyDetails): string {
  const parts: string[] = [];

  // Yearly results: Sales, Net Profit, Operating Profit, EPS
  const yearly = details.yearly_results_data;
  if (yearly && yearly.length > 0) {
    for (const [labels, label] of [
      [['sales', 'revenue'], 'Sales'],
      [['net profit'], 'Net Profit'],
      [['operating profit'], 'Operating Profit'],
      [['eps in rs', 'eps', 'earnings per share'], 'EPS'],
    ] as [string[], string][]) {
      const row = findMetricRow(labels, yearly);
      if (row) parts.push(`${label}: ${extractRecentValues(row, 5)}`);
    }
  }

  // Cash flow: Operating CF, FCF
  const cf = details.cashflow_statements;
  if (cf && cf.length > 0) {
    for (const [labels, label] of [
      [['cash from operating'], 'Cash from Ops'],
      [['free cash flow'], 'Free Cash Flow'],
    ] as [string[], string][]) {
      const row = findMetricRow(labels, cf);
      if (row) parts.push(`${label}: ${extractRecentValues(row, 4)}`);
    }
  }

  // Growth CAGRs
  if (details.sales_growth) {
    const vals = Object.entries(details.sales_growth).map(([k, v]) => `${k}: ${v}`).join(', ');
    if (vals) parts.push(`Sales Growth CAGR: ${vals}`);
  }
  if (details.profit_growth) {
    const vals = Object.entries(details.profit_growth).map(([k, v]) => `${k}: ${v}`).join(', ');
    if (vals) parts.push(`Profit Growth CAGR: ${vals}`);
  }

  // Balance sheet highlights
  const bs = details.balance_sheets;
  if (bs && bs.length > 0) {
    for (const [labels, label] of [
      [['borrowings'], 'Borrowings'],
      [['equity', 'share capital'], 'Equity'],
    ] as [string[], string][]) {
      const row = findMetricRow(labels, bs);
      if (row) parts.push(`${label}: ${extractRecentValues(row, 3)}`);
    }
  }

  // Valuation ratios
  if (details.ev_to_ebitda) parts.push(`EV/EBITDA: ${details.ev_to_ebitda}`);
  if (details.market_cap_to_sales) parts.push(`MCap/Sales: ${details.market_cap_to_sales}`);

  const result = parts.join('. ');
  return result.length > 1500 ? result.substring(0, 1500) + '...' : result;
}

// ── Build insights context string for AI prompt ──
function buildInsightsContext(insights: CompanyInsights): string {
  const parts: string[] = [];
  const truncate = (s: string, max: number) => s.length > max ? s.substring(0, max) + '...' : s;

  if (insights.business_overview) parts.push(`Business: ${truncate(insights.business_overview, 300)}`);
  if (insights.future_plans) parts.push(`Future Plans: ${truncate(insights.future_plans, 300)}`);
  if (insights.management_guidance) parts.push(`Management Guidance: ${truncate(insights.management_guidance, 300)}`);

  return parts.join('\n');
}

const PortfolioStockAnalysis: React.FC<Props> = ({ holding, holdings, colors, onSelectStock }) => {
  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails | null>(null);
  const [, setAiResponse] = useState<string>('');
  const [verdict, setVerdict] = useState<{ label: string; color: string; reason: string } | null>(null);
  const [sections, setSections] = useState<AnalysisSection[]>([]);
  const [loadingPhase, setLoadingPhase] = useState<'idle' | 'company' | 'ai' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [dcfEstimate, setDcfEstimate] = useState<{
    intrinsicValue: number | null;
    marginOfSafety: number | null;
    valuationLabel: string;
  } | null>(null);
  const [reverseDcf, setReverseDcf] = useState<{
    impliedGrowthRate: number | null;
    historicalGrowthRate: number | null;
    verdict: string; // 'Too High' | 'Reasonable' | 'Too Low'
  } | null>(null);
  const [scenarioData, setScenarioData] = useState<{
    bear: { targetPrice: number | null; percentChange: number | null; rationale: string } | null;
    base: { targetPrice: number | null; percentChange: number | null; rationale: string } | null;
    bull: { targetPrice: number | null; percentChange: number | null; rationale: string } | null;
  } | null>(null);
  const [showInsights, setShowInsights] = useState(false);

  const totalPortfolioValue = holdings.reduce((sum, h) => sum + h.current_value, 0);

  const analyzeStock = useCallback(async (h: PortfolioHolding) => {
    setCompanyDetails(null);
    setAiResponse('');
    setVerdict(null);
    setSections([]);
    setError(null);
    setDcfEstimate(null);
    setReverseDcf(null);
    setScenarioData(null);
    setShowInsights(false);
    setLoadingPhase('company');

    let details: CompanyDetails | null = null;

    // Step 1: Get company details
    try {
      const result = await getCompanyDetails(h.stock_name || h.trading_symbol);
      if (result.status === 'success' && result.data) {
        details = result.data;
        setCompanyDetails(details);
      }
    } catch (err) {
      console.error('Failed to fetch company details:', err);
    }

    // Step 2: AI analysis
    setLoadingPhase('ai');
    try {
      const weight = totalPortfolioValue > 0
        ? ((h.current_value / totalPortfolioValue) * 100).toFixed(1)
        : '0';

      const contextParts = [
        `Stock: ${h.stock_name || h.trading_symbol}`,
        `Holding: ${h.quantity} shares at avg price ₹${h.average_price.toFixed(2)}`,
        `Current price: ₹${h.last_price.toFixed(2)}, P&L: ${h.pnl_pct >= 0 ? '+' : ''}${h.pnl_pct.toFixed(1)}%`,
        `Portfolio weight: ${weight}%`,
      ];
      if (details) {
        if (details.sector) contextParts.push(`Sector: ${details.sector}`);
        if (details.price_to_earnings) contextParts.push(`P/E: ${details.price_to_earnings}`);
        if (details.roe) contextParts.push(`ROE: ${details.roe}%`);
        if (details.roce) contextParts.push(`ROCE: ${details.roce}%`);
        if (details.market_cap) {
          const mcap = details.market_cap >= 10000000
            ? (details.market_cap / 10000000).toFixed(0) + ' Cr'
            : details.market_cap.toLocaleString();
          contextParts.push(`Market Cap: ₹${mcap}`);
        }
      }

      // Add financial context from detailed tables
      let financialCtx = '';
      if (details) {
        financialCtx = buildFinancialContext(details);
      }

      // Add company insights context
      let insightsCtx = '';
      if (details?.company_insights) {
        insightsCtx = buildInsightsContext(details.company_insights);
      }

      const prompt = `${contextParts.join('. ')}.${financialCtx ? `\n\nFinancial Data:\n${financialCtx}` : ''}${insightsCtx ? `\n\nCompany Insights:\n${insightsCtx}` : ''}\n\nProvide a structured analysis with these sections:\n1. **Verdict**: Start with HOLD, ADD, or REDUCE in caps, followed by a one-line reason\n2. **Strengths**: Key positives\n3. **Concerns**: Key risks or negatives\n4. **DCF Valuation**: Estimate intrinsic value per share using a simplified DCF model. State your assumptions (revenue growth rate, discount rate/WACC, terminal growth rate). Calculate and show the estimated intrinsic value in ₹. Compare to current price and state the margin of safety percentage. Conclude whether the stock is Undervalued, Fairly Valued, or Overvalued.\n5. **Reverse DCF**: Given the current market price of ₹${h.last_price.toFixed(2)}, work backwards to determine the implied revenue growth rate the market is pricing in. Compare this to the company's historical revenue CAGR. Conclude whether market expectations are Too High, Reasonable, or Too Low. You MUST format exactly as:\nImplied Growth Rate: X%\nHistorical Growth Rate: Y%\nMarket Expectations: [Too High / Reasonable / Too Low]\n6. **Scenario Analysis**: Provide 12-month bear, base, and bull case target prices. You MUST format exactly as:\nBear Case: ₹X,XXX (-Y%) - one line rationale\nBase Case: ₹X,XXX (+Y%) - one line rationale\nBull Case: ₹X,XXX (+Y%) - one line rationale\n7. **Entry Assessment**: Is the current holding price good?\n8. **Growth Triggers**: Future catalysts\n9. **Summary**: 2-3 sentence wrap-up`;

      const aiResult = await queryStockResearch(
        {
          query: prompt,
          company_names: [h.stock_name || h.trading_symbol],
          deep_search: true,
          return_pdf: false,
        }
      );

      const text = aiResult.llm_response || '';
      setAiResponse(text);
      parseAiResponse(text);
    } catch (err) {
      console.error('AI analysis failed:', err);
      setError('AI analysis unavailable. Showing available metrics.');
    }

    setLoadingPhase('done');
  }, [totalPortfolioValue]);

  const parseAiResponse = (text: string) => {
    // Parse verdict
    const verdictMatch = text.match(/\b(HOLD|ADD|REDUCE|BUY|SELL|ACCUMULATE)\b/i);
    if (verdictMatch) {
      const v = verdictMatch[1].toUpperCase();
      const colorMap: Record<string, string> = {
        ADD: '#22c55e', BUY: '#22c55e', ACCUMULATE: '#22c55e',
        HOLD: '#f59e0b',
        REDUCE: '#ef4444', SELL: '#ef4444',
      };
      // Extract reason: text after the verdict word until next newline
      const afterVerdict = text.substring(text.indexOf(verdictMatch[0]) + verdictMatch[0].length);
      const reasonLine = afterVerdict.split('\n')[0].replace(/^[:\-–—\s]+/, '').trim();
      setVerdict({
        label: v === 'BUY' ? 'ADD' : v === 'SELL' ? 'REDUCE' : v,
        color: colorMap[v] || '#f59e0b',
        reason: reasonLine || 'Based on current fundamentals and portfolio context.',
      });
    }

    // Parse sections
    const sectionDefs: { pattern: RegExp; title: string; color: string }[] = [
      { pattern: /\*\*Strengths?\*\*[:\s]*/i, title: 'Strengths', color: '#22c55e' },
      { pattern: /\*\*Concerns?\*\*[:\s]*/i, title: 'Concerns', color: '#ef4444' },
      { pattern: /\*\*DCF Valuation\*\*[:\s]*/i, title: 'DCF Valuation', color: '#0ea5e9' },
      { pattern: /\*\*Reverse DCF\*\*[:\s]*/i, title: 'Reverse DCF', color: '#6366f1' },
      { pattern: /\*\*Scenario Analysis\*\*[:\s]*/i, title: 'Scenario Analysis', color: '#f97316' },
      { pattern: /\*\*Entry Assessment\*\*[:\s]*/i, title: 'Entry Assessment', color: '#3b82f6' },
      { pattern: /\*\*Growth Triggers?\*\*[:\s]*/i, title: 'Growth Triggers', color: '#8b5cf6' },
      { pattern: /\*\*(?:Risks?|Key Risks?)\*\*[:\s]*/i, title: 'Risks', color: '#f59e0b' },
      { pattern: /\*\*Summary\*\*[:\s]*/i, title: 'Summary', color: '#6366f1' },
    ];

    const parsed: AnalysisSection[] = [];
    for (const def of sectionDefs) {
      const match = text.match(def.pattern);
      if (match) {
        const start = text.indexOf(match[0]) + match[0].length;
        // Find next section heading
        let end = text.length;
        for (const otherDef of sectionDefs) {
          if (otherDef === def) continue;
          const otherMatch = text.match(otherDef.pattern);
          if (otherMatch) {
            const otherStart = text.indexOf(otherMatch[0]);
            if (otherStart > start && otherStart < end) {
              end = otherStart;
            }
          }
        }
        // Also check for **Verdict** section boundary
        const verdictBoundary = text.indexOf('**Verdict', start);
        if (verdictBoundary > start && verdictBoundary < end) end = verdictBoundary;

        const content = text.substring(start, end).trim();
        if (content) {
          parsed.push({ title: def.title, content, color: def.color });
        }
      }
    }
    setSections(parsed);

    // Extract DCF numeric values from DCF section
    const dcfSection = parsed.find(s => s.title === 'DCF Valuation');
    if (dcfSection) {
      const intrinsicMatch = dcfSection.content.match(/intrinsic\s+value[^₹\d]*[₹Rs.]*\s*([\d,]+)/i);
      const mosMatch = dcfSection.content.match(/margin\s+of\s+safety[:\s]*([+-]?\d+)/i);
      const labelMatch = dcfSection.content.match(/(undervalued|fairly\s*valued|overvalued)/i);

      const iv = intrinsicMatch ? parseFloat(intrinsicMatch[1].replace(/,/g, '')) : null;
      const mos = mosMatch ? parseInt(mosMatch[1]) : null;
      const vLabel = labelMatch ? labelMatch[1].replace(/\s+/g, ' ').trim() : '';

      if (iv || mos !== null || vLabel) {
        setDcfEstimate({
          intrinsicValue: iv && !isNaN(iv) ? iv : null,
          marginOfSafety: mos !== null && !isNaN(mos) ? mos : null,
          valuationLabel: vLabel.charAt(0).toUpperCase() + vLabel.slice(1).toLowerCase(),
        });
      }
    }

    // Extract Reverse DCF data
    const reverseDcfSection = parsed.find(s => s.title === 'Reverse DCF');
    if (reverseDcfSection) {
      const impliedMatch = reverseDcfSection.content.match(/implied\s+growth\s+rate[:\s]*([+-]?\d+\.?\d*)%/i);
      const historicalMatch = reverseDcfSection.content.match(/historical\s+(?:revenue\s+)?growth\s+(?:rate|cagr)[:\s]*([+-]?\d+\.?\d*)%/i);
      const expectMatch = reverseDcfSection.content.match(/market\s+expectations?[:\s]*(too\s+high|reasonable|too\s+low)/i);

      const implied = impliedMatch ? parseFloat(impliedMatch[1]) : null;
      const historical = historicalMatch ? parseFloat(historicalMatch[1]) : null;
      const expectVerdict = expectMatch ? expectMatch[1].replace(/\s+/g, ' ').trim() : '';

      if (implied !== null || historical !== null || expectVerdict) {
        setReverseDcf({
          impliedGrowthRate: implied !== null && !isNaN(implied) ? implied : null,
          historicalGrowthRate: historical !== null && !isNaN(historical) ? historical : null,
          verdict: expectVerdict.charAt(0).toUpperCase() + expectVerdict.slice(1).toLowerCase(),
        });
      }
    }

    // Extract Scenario Analysis data
    const scenarioSection = parsed.find(s => s.title === 'Scenario Analysis');
    if (scenarioSection) {
      const extractScenario = (label: string) => {
        const re = new RegExp(
          label + `\\s*Case[:\\s]*₹([\\d,]+)\\s*\\(([+-]?\\d+\\.?\\d*)%\\)\\s*[-–—]?\\s*(.*)`,
          'i'
        );
        const match = scenarioSection.content.match(re);
        if (match) {
          return {
            targetPrice: parseFloat(match[1].replace(/,/g, '')),
            percentChange: parseFloat(match[2]),
            rationale: match[3].trim().split('\n')[0].trim(),
          };
        }
        // Fallback: just price without percentage
        const reFallback = new RegExp(label + `\\s*Case[:\\s]*₹([\\d,]+)`, 'i');
        const fallback = scenarioSection.content.match(reFallback);
        if (fallback) {
          return {
            targetPrice: parseFloat(fallback[1].replace(/,/g, '')),
            percentChange: null,
            rationale: '',
          };
        }
        return null;
      };

      const bear = extractScenario('Bear');
      const base = extractScenario('Base');
      const bull = extractScenario('Bull');

      if (bear || base || bull) {
        setScenarioData({ bear, base, bull });
      }
    }
  };

  useEffect(() => {
    if (holding) {
      analyzeStock(holding);
    }
  }, [holding, analyzeStock]);

  // Render markdown-like text simply
  const renderText = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;

      // Bold
      const withBold = trimmed.split(/\*\*(.*?)\*\*/g).map((part, j) =>
        j % 2 === 1 ? <strong key={j}>{part}</strong> : part
      );

      // Bullet point
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        return (
          <div key={i} className="flex gap-2 ml-2 mb-1">
            <span style={{ color: colors.grayScale500 }}>&#8226;</span>
            <span>{withBold.slice(0).map((p, idx) => idx === 0 && typeof p === 'string' ? p.replace(/^[-*]\s*/, '') : p)}</span>
          </div>
        );
      }

      return <div key={i} className="mb-1">{withBold}</div>;
    });
  };

  // Empty state — no stock selected
  if (!holding) {
    return (
      <div>
        <CText type="B18" color={colors.textColor} style={{ marginBottom: 4 }}>
          Get an AI verdict on any stock
        </CText>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 6 }}>
          Select a holding for a detailed analysis: HOLD, ADD, or REDUCE verdict with strengths, concerns, entry assessment, and growth triggers.
        </CText>
        <CText type="r12" color={colors.grayScale400} style={{ marginBottom: 16 }}>
          Powered by real financial data — P/E, ROE, ROCE, market cap, and sector context.
        </CText>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {holdings.map((h) => (
            <button
              key={h.id}
              onClick={() => onSelectStock(h)}
              className="rounded-xl p-4 border text-left transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: colors.inputBackground,
                borderColor: colors.borderColor,
              }}
            >
              <div className="flex items-center justify-between">
                <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                  {h.trading_symbol}
                </CText>
                <CText type="r12" color={h.pnl >= 0 ? colors.green : colors.alertColor}>
                  {h.pnl_pct >= 0 ? '+' : ''}{h.pnl_pct.toFixed(1)}%
                </CText>
              </div>
              {h.stock_name && h.stock_name !== h.trading_symbol && (
                <CText type="r12" color={colors.grayScale500}>{h.stock_name}</CText>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const weight = totalPortfolioValue > 0
    ? ((holding.current_value / totalPortfolioValue) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-4">
      {/* A. Stock Header */}
      <div
        className="rounded-2xl p-5 border"
        style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <CText type="B20" color={colors.textColor}>
              {holding.trading_symbol}
            </CText>
            {holding.stock_name && holding.stock_name !== holding.trading_symbol && (
              <CText type="r14" color={colors.grayScale500}>{holding.stock_name}</CText>
            )}
          </div>
          <div
            className="px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: holding.pnl >= 0 ? '#22c55e20' : '#ef444420',
              color: holding.pnl >= 0 ? '#22c55e' : '#ef4444',
            }}
          >
            <span className="text-sm font-semibold">
              {holding.pnl_pct >= 0 ? '+' : ''}{holding.pnl_pct.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Sector/Industry pills */}
        {companyDetails && (
          <div className="flex flex-wrap gap-2 mb-3">
            {companyDetails.sector && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
              >
                {companyDetails.sector}
              </span>
            )}
            {companyDetails.industry && companyDetails.industry !== companyDetails.sector && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: colors.grayScale500 + '20', color: colors.grayScale500 }}
              >
                {companyDetails.industry}
              </span>
            )}
          </div>
        )}

        {/* Position info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <CText type="r10" color={colors.grayScale500}>Qty</CText>
            <CText type="M14" color={colors.textColor}>{holding.quantity}</CText>
          </div>
          <div>
            <CText type="r10" color={colors.grayScale500}>Avg Price</CText>
            <CText type="M14" color={colors.textColor}>{formatINR(holding.average_price)}</CText>
          </div>
          <div>
            <CText type="r10" color={colors.grayScale500}>Current Value</CText>
            <CText type="M14" color={colors.textColor}>{formatINR(holding.current_value)}</CText>
          </div>
          <div>
            <CText type="r10" color={colors.grayScale500}>Weight</CText>
            <CText type="M14" color={colors.textColor}>{weight}%</CText>
          </div>
        </div>
      </div>

      {/* Loading states */}
      {loadingPhase === 'company' && (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full" style={{ backgroundColor: colors.primary + '30' }} />
            <CText type="r14" color={colors.grayScale500}>Fetching company data...</CText>
          </div>
        </div>
      )}

      {loadingPhase === 'ai' && (
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: colors.inputBackground }}>
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full" style={{ backgroundColor: colors.primary + '30' }} />
            <CText type="r14" color={colors.grayScale500}>Running AI analysis...</CText>
          </div>
        </div>
      )}

      {/* B. AI Verdict Banner */}
      {verdict && loadingPhase === 'done' && (
        <div
          className="rounded-xl p-4 flex items-center gap-3"
          style={{ backgroundColor: verdict.color + '15', borderLeft: `4px solid ${verdict.color}` }}
        >
          <span
            className="px-3 py-1 rounded-full text-sm font-bold"
            style={{ backgroundColor: verdict.color, color: '#fff' }}
          >
            {verdict.label}
          </span>
          <CText type="r14" color={colors.textColor}>{verdict.reason}</CText>
        </div>
      )}

      {/* C. Key Metrics Grid */}
      {companyDetails && loadingPhase === 'done' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: 'Market Cap', value: companyDetails.market_cap ? formatINR(companyDetails.market_cap) : 'N/A' },
            { label: 'P/E Ratio', value: companyDetails.price_to_earnings ?? 'N/A' },
            { label: 'ROE', value: companyDetails.roe != null ? `${companyDetails.roe}%` : 'N/A' },
            { label: 'ROCE', value: companyDetails.roce != null ? `${companyDetails.roce}%` : 'N/A' },
            { label: 'Dividend Yield', value: companyDetails.dividend_yield != null ? `${companyDetails.dividend_yield}%` : 'N/A' },
            { label: 'P/B Ratio', value: companyDetails.price_to_book ?? 'N/A' },
          ].map((m) => (
            <div
              key={m.label}
              className="rounded-xl p-3 border"
              style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
            >
              <CText type="r10" color={colors.grayScale500}>{m.label}</CText>
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>{String(m.value)}</CText>
            </div>
          ))}
        </div>
      )}

      {/* D. Company Insights (collapsible) */}
      {companyDetails?.company_insights && loadingPhase === 'done' && (() => {
        const ins = companyDetails.company_insights;
        const hasAny = ins.business_overview || ins.future_plans || ins.management_guidance;
        if (!hasAny) return null;
        return (
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
          >
            <button
              onClick={() => setShowInsights(prev => !prev)}
              className="w-full flex items-center justify-between p-4 text-left"
              style={{ backgroundColor: colors.inputBackground }}
            >
              <div className="flex items-center gap-2">
                {React.createElement(FiBookOpen as any, { size: 16, color: colors.primary })}
                <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                  Company Insights
                </CText>
              </div>
              {React.createElement(
                (showInsights ? FiChevronUp : FiChevronDown) as any,
                { size: 16, color: colors.grayScale500 }
              )}
            </button>
            {showInsights && (
              <div className="px-4 pb-4 space-y-3" style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                {ins.business_overview && (
                  <div className="pt-3">
                    <CText type="r12" color={colors.grayScale500} style={{ fontWeight: 600, marginBottom: 4 }}>
                      Business Overview
                    </CText>
                    <CText type="r14" color={colors.textColor} style={{ lineHeight: 1.6 }}>
                      {ins.business_overview}
                    </CText>
                  </div>
                )}
                {ins.future_plans && (
                  <div>
                    <CText type="r12" color={colors.grayScale500} style={{ fontWeight: 600, marginBottom: 4 }}>
                      Future Plans
                    </CText>
                    <CText type="r14" color={colors.textColor} style={{ lineHeight: 1.6 }}>
                      {ins.future_plans}
                    </CText>
                  </div>
                )}
                {ins.management_guidance && (
                  <div>
                    <CText type="r12" color={colors.grayScale500} style={{ fontWeight: 600, marginBottom: 4 }}>
                      Management Guidance
                    </CText>
                    <CText type="r14" color={colors.textColor} style={{ lineHeight: 1.6 }}>
                      {ins.management_guidance}
                    </CText>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* E. DCF Valuation Highlight */}
      {dcfEstimate && loadingPhase === 'done' && (
        <div
          className="rounded-xl p-4 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: '#0ea5e930',
            borderLeft: '4px solid #0ea5e9',
          }}
        >
          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 10 }}>
            DCF Valuation Estimate
          </CText>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {dcfEstimate.intrinsicValue != null && (
              <div>
                <CText type="r10" color={colors.grayScale500}>Intrinsic Value</CText>
                <CText type="M14" color="#0ea5e9" style={{ fontWeight: 700 }}>
                  {formatINR(dcfEstimate.intrinsicValue)}
                </CText>
              </div>
            )}
            <div>
              <CText type="r10" color={colors.grayScale500}>Current Price</CText>
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                {formatINR(holding.last_price)}
              </CText>
            </div>
            {dcfEstimate.marginOfSafety != null && (
              <div>
                <CText type="r10" color={colors.grayScale500}>Margin of Safety</CText>
                <CText
                  type="M14"
                  color={dcfEstimate.marginOfSafety >= 0 ? '#22c55e' : '#ef4444'}
                  style={{ fontWeight: 600 }}
                >
                  {dcfEstimate.marginOfSafety >= 0 ? '+' : ''}{dcfEstimate.marginOfSafety}%
                </CText>
              </div>
            )}
            {dcfEstimate.valuationLabel && (
              <div className="flex items-end">
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor:
                      /undervalued/i.test(dcfEstimate.valuationLabel) ? '#22c55e20' :
                      /overvalued/i.test(dcfEstimate.valuationLabel) ? '#ef444420' : '#f59e0b20',
                    color:
                      /undervalued/i.test(dcfEstimate.valuationLabel) ? '#22c55e' :
                      /overvalued/i.test(dcfEstimate.valuationLabel) ? '#ef4444' : '#f59e0b',
                  }}
                >
                  {dcfEstimate.valuationLabel}
                </span>
              </div>
            )}
          </div>
          <CText type="r10" color={colors.grayScale400} style={{ fontStyle: 'italic' }}>
            AI-generated estimate. Not investment advice.
          </CText>
        </div>
      )}

      {/* E2. Reverse DCF Highlight */}
      {reverseDcf && loadingPhase === 'done' && (
        <div
          className="rounded-xl p-4 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: '#6366f130',
            borderLeft: '4px solid #6366f1',
          }}
        >
          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 10 }}>
            Reverse DCF — What is the Market Pricing In?
          </CText>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {reverseDcf.impliedGrowthRate != null && (
              <div>
                <CText type="r10" color={colors.grayScale500}>Implied Growth Rate</CText>
                <CText type="M14" color="#6366f1" style={{ fontWeight: 700 }}>
                  {reverseDcf.impliedGrowthRate >= 0 ? '+' : ''}{reverseDcf.impliedGrowthRate}%
                </CText>
              </div>
            )}
            {reverseDcf.historicalGrowthRate != null && (
              <div>
                <CText type="r10" color={colors.grayScale500}>Historical Growth Rate</CText>
                <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                  {reverseDcf.historicalGrowthRate >= 0 ? '+' : ''}{reverseDcf.historicalGrowthRate}%
                </CText>
              </div>
            )}
            {reverseDcf.verdict && (
              <div className="flex items-end">
                <span
                  className="px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{
                    backgroundColor:
                      /too\s*low/i.test(reverseDcf.verdict) ? '#22c55e20' :
                      /too\s*high/i.test(reverseDcf.verdict) ? '#ef444420' : '#f59e0b20',
                    color:
                      /too\s*low/i.test(reverseDcf.verdict) ? '#22c55e' :
                      /too\s*high/i.test(reverseDcf.verdict) ? '#ef4444' : '#f59e0b',
                  }}
                >
                  {reverseDcf.verdict}
                </span>
              </div>
            )}
          </div>
          {/* Comparison bar */}
          {reverseDcf.impliedGrowthRate != null && reverseDcf.historicalGrowthRate != null && (
            <div className="mb-3">
              <div className="flex justify-between mb-1">
                <CText type="r10" color={colors.grayScale500}>Historical</CText>
                <CText type="r10" color={colors.grayScale500}>Implied</CText>
              </div>
              <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.borderColor }}>
                {(() => {
                  const maxVal = Math.max(Math.abs(reverseDcf.impliedGrowthRate), Math.abs(reverseDcf.historicalGrowthRate), 1);
                  const histPct = Math.min((Math.abs(reverseDcf.historicalGrowthRate) / (maxVal * 1.2)) * 100, 100);
                  const implPct = Math.min((Math.abs(reverseDcf.impliedGrowthRate) / (maxVal * 1.2)) * 100, 100);
                  return (
                    <>
                      <div
                        className="absolute top-0 left-0 h-full rounded-full"
                        style={{ width: `${histPct}%`, backgroundColor: '#6366f160' }}
                      />
                      <div
                        className="absolute top-0 left-0 h-full rounded-full"
                        style={{
                          width: `${implPct}%`,
                          backgroundColor: reverseDcf.impliedGrowthRate > reverseDcf.historicalGrowthRate ? '#ef444490' : '#22c55e90',
                          opacity: 0.7,
                        }}
                      />
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          <CText type="r10" color={colors.grayScale400} style={{ fontStyle: 'italic' }}>
            AI-estimated reverse DCF. Not investment advice.
          </CText>
        </div>
      )}

      {/* E3. Scenario Analysis */}
      {scenarioData && loadingPhase === 'done' && (
        <div
          className="rounded-xl p-4 border"
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: '#f9731630',
            borderLeft: '4px solid #f97316',
          }}
        >
          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 10 }}>
            Scenario Analysis — 12 Month Targets
          </CText>

          {/* Range bar */}
          {scenarioData.bear?.targetPrice != null && scenarioData.bull?.targetPrice != null && (
            <div className="mb-4">
              {(() => {
                const bearPrice = scenarioData.bear!.targetPrice!;
                const bullPrice = scenarioData.bull!.targetPrice!;
                const basePrice = scenarioData.base?.targetPrice ?? (bearPrice + bullPrice) / 2;
                const range = bullPrice - bearPrice;
                const currentPos = range > 0 ? Math.min(Math.max(((holding.last_price - bearPrice) / range) * 100, 0), 100) : 50;

                return (
                  <div className="relative">
                    <div className="flex justify-between mb-1">
                      <CText type="r10" color="#ef4444">{formatINR(bearPrice)}</CText>
                      {scenarioData.base?.targetPrice != null && (
                        <CText type="r10" color="#f59e0b">{formatINR(basePrice)}</CText>
                      )}
                      <CText type="r10" color="#22c55e">{formatINR(bullPrice)}</CText>
                    </div>
                    <div
                      className="h-3 rounded-full relative"
                      style={{
                        background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e)',
                      }}
                    >
                      {/* Current price marker */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2"
                        style={{
                          left: `${currentPos}%`,
                          transform: `translate(-50%, -50%)`,
                          backgroundColor: colors.inputBackground,
                          borderColor: colors.textColor,
                        }}
                      />
                    </div>
                    <div className="mt-1" style={{ marginLeft: `${currentPos}%`, transform: 'translateX(-50%)' }}>
                      <CText type="r10" color={colors.grayScale500}>CMP: {formatINR(holding.last_price)}</CText>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* 3-column grid */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: 'bear' as const, label: 'Bear', color: '#ef4444', data: scenarioData.bear },
              { key: 'base' as const, label: 'Base', color: '#f59e0b', data: scenarioData.base },
              { key: 'bull' as const, label: 'Bull', color: '#22c55e', data: scenarioData.bull },
            ].map(({ label, color, data }) => (
              data && (
                <div
                  key={label}
                  className="rounded-lg p-3 text-center"
                  style={{ backgroundColor: color + '10' }}
                >
                  <CText type="r10" color={color} style={{ fontWeight: 600, marginBottom: 4 }}>
                    {label}
                  </CText>
                  {data.targetPrice != null && (
                    <div>
                      <CText type="M14" color={color} style={{ fontWeight: 700 }}>
                        {formatINR(data.targetPrice)}
                      </CText>
                    </div>
                  )}
                  {data.percentChange != null && (
                    <CText type="r12" color={color}>
                      ({data.percentChange >= 0 ? '+' : ''}{data.percentChange}%)
                    </CText>
                  )}
                  {data.rationale && (
                    <CText type="r10" color={colors.grayScale500} style={{ marginTop: 4, lineHeight: 1.3 }}>
                      {data.rationale.length > 60 ? data.rationale.substring(0, 60) + '...' : data.rationale}
                    </CText>
                  )}
                </div>
              )
            ))}
          </div>
          <CText type="r10" color={colors.grayScale400} style={{ fontStyle: 'italic', marginTop: 8 }}>
            AI-estimated scenarios. Not investment advice.
          </CText>
        </div>
      )}

      {/* F. AI Analysis Cards */}
      {sections.length > 0 && loadingPhase === 'done' && (
        <div className="space-y-3">
          {sections.map((section) => (
            <div
              key={section.title}
              className="rounded-xl p-4"
              style={{
                backgroundColor: colors.inputBackground,
                borderLeft: `4px solid ${section.color}`,
              }}
            >
              <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 6 }}>
                {section.title}
              </CText>
              <div style={{ color: colors.textColor, fontSize: 14, lineHeight: 1.6 }}>
                {renderText(section.content)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* G. Error state */}
      {error && loadingPhase === 'done' && (
        <div className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: '#f59e0b15' }}>
          {React.createElement(FiAlertTriangle as any, { size: 18, color: '#f59e0b' })}
          <CText type="r14" color={colors.textColor}>{error}</CText>
          <button
            onClick={() => analyzeStock(holding)}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
          >
            Retry
          </button>
        </div>
      )}

      {/* H. Expert CTA */}
      {loadingPhase === 'done' && (
        <div
          className="rounded-2xl p-5 text-center"
          style={{
            background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
          }}
        >
          <CText type="B16" color={colors.white} style={{ marginBottom: 4 }}>
            Discuss {holding.stock_name || holding.trading_symbol} with an expert
          </CText>
          <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 12 }}>
            Get personalized buy/sell advice from SEBI-registered analysts
          </CText>
          <button
            onClick={() => navigate('/experts')}
            className="px-5 py-2.5 rounded-xl font-medium text-sm transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: colors.white, backdropFilter: 'blur(8px)' }}
          >
            {React.createElement(FiUsers as any, { size: 14, style: { display: 'inline', marginRight: 6, verticalAlign: 'middle' } })}
            Find an Expert
          </button>
        </div>
      )}
    </div>
  );
};

export default PortfolioStockAnalysis;
